// @ts-nocheck
// TODO: tighten types after migration
import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import "./AdminHome.css";
import "./CreateDocument.css";
import { useNavPinned } from "./useNavPinned";
import AdminTopNav from "./AdminTopNav";
import { API_ENDPOINTS } from "../config/api";
import { getAuthToken } from "../utils/auth";
import TipTapEditor from "./TipTapEditor";
import DynamicField from "./DynamicField";
import {
  getCollectionSchema,
  isFieldRequired,
  getFieldType,
  getFieldLabel,
  validateRequiredFields,
  getFieldOrder,
} from "./collectionSchemas";
// eslint-disable-next-line no-unused-vars
import SidebarAd from "../Blog/SidebarAd";


export default function CreateDocument() {
  const { collectionName } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [navPinned, setNavPinned] = useNavPinned();
  const [isScheduled, setIsScheduled] = useState(false);
  const [loading, setLoading] = useState(false); // eslint-disable-line no-unused-vars
  const [error, setError] = useState("");
  const [newData, setNewData] = useState({});
  const isInitialized = useRef(false);
  const lastCollection = useRef("");
  const [isSaving, setIsSaving] = useState(false);
  const [showFixedBar, setShowFixedBar] = useState(true);
  const _buttonBarObserver = useRef(null);
  const buttonBarRef = useCallback((node) => {
    if (_buttonBarObserver.current) {
      _buttonBarObserver.current.disconnect();
      _buttonBarObserver.current = null;
    }
    if (!node) return;
    _buttonBarObserver.current = new IntersectionObserver(
      ([entry]) => setShowFixedBar(!entry.isIntersecting),
      { threshold: 0 }
    );
    _buttonBarObserver.current.observe(node);
  }, []);
  const [validationErrors, setValidationErrors] = useState([]);
  const [authorOptions, setAuthorOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [allPostTopicOptions, setAllPostTopicOptions] = useState([]); // Store all PostTopics
  const [postTopicOptions, setPostTopicOptions] = useState([]); // Filtered PostTopics based on category
  const [postTopicCategoryOptions, setPostTopicCategoryOptions] = useState([]);
  const [productOptions, setProductOptions] = useState([]); // Store ProductDetails for ProductPrices
  const [regionOptions, setRegionOptions] = useState([]); // Store Regions for ProductPrices
  const [multiPrices, setMultiPrices] = useState([]); // Store multi-price entries
  const [pendingTranscriptFile, setPendingTranscriptFile] = useState(null);

  // Get user role from localStorage and restrict access for staff
  const getUserRole = () => {
    try {
      const userStr = sessionStorage.getItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        return user.role || null;
      }
    } catch (e) {
      console.error("Error parsing user data:", e);
    }
    return null;
  };

  const userRole = getUserRole();
  const isAdmin = userRole === "admin"; // eslint-disable-line no-unused-vars
  const isStaff = userRole === "staff";

  // Redirect staff users - they don't have permission to create documents
  useEffect(() => {
    if (isStaff) {
      // Detect database type from URL to redirect back to collection
      let databaseType = "creddb";
      if (location.pathname.includes("/blogdb/")) {
        databaseType = "blogdb";
      } else if (location.pathname.includes("/newsletterdb/")) {
        databaseType = "newsletterdb";
      } else if (location.pathname.includes("/podcastdb/")) {
        databaseType = "podcastdb";
      } else if (location.pathname.includes("/storedb/")) {
        databaseType = "storedb";
      } else if (location.pathname.includes("/sponsorshipdb/")) {
        databaseType = "sponsorshipdb";
      } else if (location.pathname.includes("/userthoughts/")) {
        databaseType = "userthoughts";
      } else if (location.pathname.includes("/newsroomdb/")) {
        databaseType = "newsroomdb";
      } else if (location.pathname.includes("/learnwithai/")) {
        databaseType = "learnwithai";
      } else if (location.pathname.includes("/socialmediadb/")) {
        databaseType = "socialmediadb";
      } else if (location.pathname.includes("/affiliateproductsdb/")) {
        databaseType = "affiliateproductsdb";
      }
      navigate(`/admin/database/${databaseType}/${collectionName}`);
    }
  }, [isStaff, navigate, location.pathname, collectionName]);

  // Detect database from URL path
  let databaseType = "creddb";
  let databaseName = "CredDb";
  if (location.pathname.includes("/blogdb/")) {
    databaseType = "blogdb";
    databaseName = "BlogDb";
  } else if (location.pathname.includes("/newsletterdb/")) {
    databaseType = "newsletterdb";
    databaseName = "NewsLetterDb";
  } else if (location.pathname.includes("/podcastdb/")) {
    databaseType = "podcastdb";
    databaseName = "PodcastDb";
  } else if (location.pathname.includes("/storedb/")) {
    databaseType = "storedb";
    databaseName = "StoreDb";
  } else if (location.pathname.includes("/sponsorshipdb/")) {
    databaseType = "sponsorshipdb";
    databaseName = "SponsorshipDb";
  } else if (location.pathname.includes("/userthoughts/")) {
    databaseType = "userthoughts";
    databaseName = "UserThoughts";
  } else if (location.pathname.includes("/newsroomdb/")) {
    databaseType = "newsroomdb";
    databaseName = "NewsRoomDb";
  } else if (location.pathname.includes("/learnwithai/")) {
    databaseType = "learnwithai";
    databaseName = "LearnWithAI";
  } else if (location.pathname.includes("/socialmediadb/")) {
    databaseType = "socialmediadb";
    databaseName = "SocialMediaDb";
  } else if (location.pathname.includes("/affiliateproductsdb/")) {
    databaseType = "affiliateproductsdb";
    databaseName = "AffiliateProductsDb";
  }

  // Builds the correct API URL for document-level operations based on which DB this collection belongs to.
  const getDocUrl = (collection, docId) => {
    if (databaseType === "newsroomdb") {
      const base = `${API_ENDPOINTS.ADMIN.DATABASE_NEWSROOMDB}/${encodeURIComponent(collection)}`;
      return docId ? `${base}/${encodeURIComponent(docId)}` : base;
    }
    if (databaseType === "learnwithai") {
      const base = `${API_ENDPOINTS.ADMIN.DATABASE_LEARNWITHAI}/${encodeURIComponent(collection)}`;
      return docId ? `${base}/${encodeURIComponent(docId)}` : base;
    }
    if (databaseType === "socialmediadb") {
      const base = `${API_ENDPOINTS.ADMIN.DATABASE_SOCIALMEDIADB}/${encodeURIComponent(collection)}`;
      return docId ? `${base}/${encodeURIComponent(docId)}` : base;
    }
    if (databaseType === "affiliateproductsdb") {
      const base = `${API_ENDPOINTS.ADMIN.DATABASE_AFFILIATEPRODUCTSDB}/${encodeURIComponent(collection)}`;
      return docId ? `${base}/${encodeURIComponent(docId)}` : base;
    }
    return docId
      ? API_ENDPOINTS.ADMIN.COLLECTION_GET(collection, docId)
      : API_ENDPOINTS.ADMIN.COLLECTION_CREATE(collection);
  };

  const getAuthHeaders = () => {
    const token = sessionStorage.getItem("token");
    return {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  // Save current unsaved form state to sessionStorage before navigating away
  const saveFormStateToSession = useCallback(() => {
    const key = `createDoc_${collectionName}_unsaved`;
    sessionStorage.setItem(key, JSON.stringify(newData));
  }, [newData, collectionName]);

  // Reset form data when collection changes (React Router reuses component instance between routes)
  // and restore any unsaved state saved before navigating away to create a related item
  useEffect(() => {
    // If we've already initialized for this collection in this session, don't do it again
    // This prevents Strict Mode double-mount from clearing the session storage on the first mount
    // and then finding it empty on the second mount, resulting in data loss.
    if (isInitialized.current && lastCollection.current === collectionName) return;

    const key = `createDoc_${collectionName}_unsaved`;
    const savedStr = sessionStorage.getItem(key);
    if (savedStr) {
      try {
        const saved = JSON.parse(savedStr);
        setNewData(saved);
      } catch {
        setNewData({});
      }
      sessionStorage.removeItem(key);
    } else {
      // Always reset to empty when collection changes to prevent stale data from previous collection
      setNewData({});
    }
    
    isInitialized.current = true;
    lastCollection.current = collectionName;
  }, [collectionName]);

  // Fetch dropdown options for Author, Category, PostTopic, and ProductDetails
  useEffect(() => {
    const fetchReferenceData = async () => {
      const headers = getAuthHeaders();
      const needsRefData = collectionName === "BlogContent" || collectionName === "PostTopics" || collectionName === "ProductPrices";
      if (needsRefData) {
        setLoading(true);
      }

      try {
        // Auto-fetch next postNumber for BlogContent if not already set
        if (collectionName === "BlogContent") {
          try {
            const blogRes = await fetch(
              API_ENDPOINTS.ADMIN.DATABASE_COLLECTION("BlogContent"),
              {
                headers,
                credentials: "include",
                cache: "no-store",
              },
            );
            if (blogRes.ok) {
              const blogJson = await blogRes.json();
              const blogs = Array.isArray(blogJson.data) ? blogJson.data : [];
              let maxNum = 0;
              blogs.forEach((b) => {
                const num = typeof b.postNumber === "number" ? b.postNumber : Number(b.postNumber);
                if (!isNaN(num) && num > maxNum) {
                  maxNum = num;
                }
              });
              const nextPostNumber = maxNum + 1;
              setNewData((prev) => {
                if (prev.postNumber === undefined || prev.postNumber === null || prev.postNumber === "") {
                  return {
                    ...prev,
                    postNumber: nextPostNumber,
                  };
                }
                return prev;
              });
            }
          } catch (err) {
            console.error("Error fetching next postNumber for BlogContent:", err);
          }
        }

        // Fetch Category for BlogContent or PostTopics
        if (
          collectionName === "BlogContent" ||
          collectionName === "PostTopics"
        ) {
          try {
            const categoryRes = await fetch(
              API_ENDPOINTS.ADMIN.DATABASE_COLLECTION("Category"),
              {
                headers,
                credentials: "include",
                cache: "no-store",
              },
            );
            if (categoryRes.ok) {
              const categoryJson = await categoryRes.json();
              const categories = (Array.isArray(categoryJson.data)
                ? categoryJson.data
                : []
              ).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
              if (collectionName === "BlogContent") {
                setCategoryOptions(categories);
              } else if (collectionName === "PostTopics") {
                setPostTopicCategoryOptions(categories);
              }
            }
          } catch (err) {
            console.error("Error fetching Category:", err);
          }
        }

        // Fetch AuthorDetails and PostTopics only for BlogContent
        if (collectionName === "BlogContent") {
          // Fetch AuthorDetails
          try {
            const authorRes = await fetch(
              API_ENDPOINTS.ADMIN.DATABASE_COLLECTION("AuthorDetails"),
              {
                headers,
                credentials: "include",
                cache: "no-store",
              },
            );
            if (authorRes.ok) {
              const authorJson = await authorRes.json();
              const authors = (Array.isArray(authorJson.data) ? authorJson.data : [])
                .sort((a, b) => (a.name || a.authorName || '').localeCompare(b.name || b.authorName || ''));
              setAuthorOptions(authors);
            }
          } catch (err) {
            console.error("Error fetching AuthorDetails:", err);
          }

          // Fetch PostTopics
          try {
            const postTopicRes = await fetch(
              API_ENDPOINTS.ADMIN.DATABASE_COLLECTION("PostTopics"),
              {
                headers,
                credentials: "include",
                cache: "no-store",
              },
            );
            if (postTopicRes.ok) {
              const postTopicJson = await postTopicRes.json();
              const allTopics = (Array.isArray(postTopicJson.data)
                ? postTopicJson.data
                : []
              ).sort((a, b) => (a.topic || a.name || '').localeCompare(b.topic || b.name || ''));
              setAllPostTopicOptions(allTopics);
              // Initially set all topics, will be filtered when category is selected
              setPostTopicOptions(allTopics);
            }
          } catch (err) {
            console.error("Error fetching PostTopics:", err);
          }
        }

        // Fetch ProductDetails for ProductPrices collection (product dropdown)
        if (collectionName === "ProductPrices") {
          try {
            const productRes = await fetch(
              API_ENDPOINTS.ADMIN.DATABASE_COLLECTION("ProductDetails"),
              {
                headers,
                credentials: "include",
                cache: "no-store",
              },
            );
            if (productRes.ok) {
              const productJson = await productRes.json();
              const products = (Array.isArray(productJson.data) ? productJson.data : [])
                .sort((a, b) => (a.title || a.name || '').localeCompare(b.title || b.name || ''));
              setProductOptions(products);
            }
          } catch (err) {
            console.error("Error fetching ProductDetails:", err);
          }

          // Fetch Regions for multi-price entry
          try {
            const regionRes = await fetch(
              API_ENDPOINTS.ADMIN.DATABASE_COLLECTION("Regions"),
              {
                headers,
                credentials: "include",
                cache: "no-store",
              },
            );
            if (regionRes.ok) {
              const regionJson = await regionRes.json();
              const regions = (Array.isArray(regionJson.data) ? regionJson.data : [])
                .sort((a, b) => (a.name || a.country || '').localeCompare(b.name || b.country || ''));
              setRegionOptions(regions);
              
              // Initialize with one empty price entry if no regions selected yet
              setMultiPrices([{
                countryCode: '',
                currency: '',
                currencySymbol: '',
                basePrice: '',
                offerPrice: '',
                isActive: true,
                taxIncluded: true
              }]);
            }
          } catch (err) {
            console.error("Error fetching Regions:", err);
          }
        }
      } catch (err) {
        console.error("Error fetching reference data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchReferenceData();
  }, [collectionName]);

  const handleInputChange = (key, value) => {
    setNewData((prev) => {
      const updated = {
        ...prev,
        [key]: value,
      };

      // If category is changed in BlogContent, filter PostTopics
      if (collectionName === "BlogContent" && key === "category") {
        filterPostTopicsByCategory(value, allPostTopicOptions);
        // Clear postTopic if it doesn't match the new category
        if (prev.postTopic) {
          const selectedCategoryId =
            typeof value === "object" && value._id
              ? String(value._id)
              : String(value);
          const currentPostTopicId =
            typeof prev.postTopic === "object" && prev.postTopic._id
              ? String(prev.postTopic._id)
              : String(prev.postTopic);
          const filteredTopics = allPostTopicOptions.filter((topic) => {
            const topicCategoryId = topic.whichCategory
              ? typeof topic.whichCategory === "object" &&
                topic.whichCategory._id
                ? String(topic.whichCategory._id)
                : String(topic.whichCategory)
              : null;
            return topicCategoryId === selectedCategoryId;
          });
          const topicStillValid = filteredTopics.some(
            (topic) => String(topic._id) === currentPostTopicId,
          );
          if (!topicStillValid) {
            updated.postTopic = "";
          }
        }
      }

      return updated;
    });
    // Clear validation error for this field
    setValidationErrors((prev) =>
      prev.filter((err) => !err.includes(getFieldLabel(collectionName, key))),
    );
  };

  // Filter PostTopics based on selected category
  const filterPostTopicsByCategory = (selectedCategory, allTopics) => {
    if (!selectedCategory) {
      // If no category selected, show all topics
      setPostTopicOptions(allTopics);
      return;
    }

    const categoryId =
      typeof selectedCategory === "object" && selectedCategory._id
        ? String(selectedCategory._id)
        : String(selectedCategory);

    const filtered = allTopics
      .filter((topic) => {
        if (!topic.whichCategory) return false;
        const topicCategoryId =
          typeof topic.whichCategory === "object" && topic.whichCategory._id
            ? String(topic.whichCategory._id)
            : String(topic.whichCategory);
        return topicCategoryId === categoryId;
      })
      .sort((a, b) => (a.topic || a.name || '').localeCompare(b.topic || b.name || ''));

    setPostTopicOptions(filtered);
  };

  // Multi-price helper functions for ProductPrices
  const addPriceEntry = () => {
    setMultiPrices(prev => [
      ...prev,
      {
        countryCode: '',
        currency: '',
        currencySymbol: '',
        basePrice: '',
        offerPrice: '',
        isActive: true,
        taxIncluded: true
      }
    ]);
  };

  const removePriceEntry = (index) => {
    if (multiPrices.length <= 1) {
      alert("At least one price entry is required");
      return;
    }
    setMultiPrices(prev => prev.filter((_, i) => i !== index));
  };

  const handleMultiPriceChange = (index, key, value) => {
    setMultiPrices(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [key]: value };
      
      // If countryCode is changed, auto-fill currency and symbol
      if (key === 'countryCode') {
        const region = regionOptions.find(r => (r.country === value || r.code === value));
        if (region) {
          updated[index].currency = region.currency;
          updated[index].currencySymbol = region.symbol || region.currencySymbol || '';
        }
      }
      
      return updated;
    });
  };

  // Filter PostTopics when category changes
  useEffect(() => {
    if (collectionName === "BlogContent" && allPostTopicOptions.length > 0) {
      filterPostTopicsByCategory(newData.category, allPostTopicOptions);
    }
  }, [newData.category, allPostTopicOptions, collectionName]);



  // Initialize boolean fields with false (No) as default for BlogContent, Regions, ProductPrices
  // Also initialize array fields to empty array
  useEffect(() => {
    if (collectionName === "BlogContent") {
      const booleanFields = ["isPublished", "priorityPost", "noIndex"];
      booleanFields.forEach((field) => {
        if (newData[field] === undefined) {
          setNewData((prev) => ({
            ...prev,
            [field]: false,
          }));
        }
      });
    }

    // Initialize boolean fields for Regions
    if (collectionName === "Regions") {
      if (newData.isActive === undefined) {
        setNewData((prev) => ({
          ...prev,
          isActive: true,
        }));
      }
    }

    // Initialize boolean fields for ProductPrices
    if (collectionName === "ProductPrices") {
      const booleanFields = ["taxIncluded", "isActive"];
      booleanFields.forEach((field) => {
        if (newData[field] === undefined) {
          setNewData((prev) => ({
            ...prev,
            [field]: field === "isActive" ? true : false,
          }));
        }
      });
    }

    // Initialize array fields and boolean fields based on schema
    const schema = getCollectionSchema(collectionName);
    if (schema && schema.fieldTypes) {
      Object.entries(schema.fieldTypes).forEach(([key, type]) => {
        // Initialize arrays
        if (type === "array" && newData[key] === undefined) {
          setNewData((prev) => ({
            ...prev,
            [key]: [],
          }));
        }
        
        // Initialize booleans
        if (type === 'boolean' && newData[key] === undefined) {
          // For isPublished, we often want true by default
          const defaultValue = (key === 'isPublished') ? true : false;
          setNewData(prev => ({
            ...prev,
            [key]: defaultValue
          }));
        }
      });
    }

    // Auto-initialize date/createdAt for Podcast and Blog collections
    if (collectionName === "PodcastsAudioDetails") {
      if (newData.date === undefined) {
        setNewData((prev) => ({
          ...prev,
          date: new Date().toISOString(),
          isPublished: true // Default to published for "Post Now"
        }));
      }
    } else if (collectionName === "BlogContent") {
      if (newData.createdAt === undefined) {
        setNewData((prev) => ({
          ...prev,
          createdAt: new Date().toISOString(),
          isPublished: true // Default to published for "Post Now"
        }));
      }
    }
  }, [collectionName]); // eslint-disable-line react-hooks/exhaustive-deps


  // Recursively trim leading/trailing whitespace from all string fields
  const trimStringValues = (obj) => {
    if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) return obj;
    const result = {};
    Object.keys(obj).forEach(key => {
      const val = obj[key];
      if (typeof val === 'string') {
        result[key] = val.trim();
      } else if (Array.isArray(val)) {
        result[key] = val.map(item => (typeof item === 'string' ? item.trim() : item));
      } else {
        result[key] = val;
      }
    });
    return result;
  };

  const handleCreate = async () => {
    // Auto-generate ID for CouponCode if not provided
    const dataToSubmit = trimStringValues({ ...newData });

    // Remove passwordHash for Users collection - it's handled by backend
    if (collectionName === "Users" && dataToSubmit.passwordHash !== undefined) {
      delete dataToSubmit.passwordHash;
    }

    if (
      collectionName === "CouponCode" &&
      (!dataToSubmit.id || dataToSubmit.id.trim() === "")
    ) {
      // Generate a unique ID based on timestamp and random string
      const timestamp = Date.now();
      const randomStr = Math.random()
        .toString(36)
        .substring(2, 8)
        .toUpperCase();
      dataToSubmit.id = `COUPON-${timestamp}-${randomStr}`;
    }

    // Special handling for ProductPrices - skip normal validation for multi-entry fields
    let validation;
    if (collectionName === "ProductPrices") {
        // Only validate productId for the main object, other fields are in multiPrices
        validation = { valid: !!dataToSubmit.productId, errors: dataToSubmit.productId ? [] : ["Product is required"] };
        
        // Validate multiPrices
        const multiErrors = [];
        multiPrices.forEach((price, idx) => {
            if (!price.countryCode) multiErrors.push(`Entry ${idx + 1}: Country Code is required`);
            if (!price.currency) multiErrors.push(`Entry ${idx + 1}: Currency is required`);
            if (price.basePrice === '' || price.basePrice === null) multiErrors.push(`Entry ${idx + 1}: Base Price is required`);
        });
        
        if (multiErrors.length > 0) {
            setValidationErrors(multiErrors);
            alert(`Please fix the following errors:\n${multiErrors.join("\n")}`);
            return;
        }
    } else {
        validation = validateRequiredFields(collectionName, dataToSubmit);
    }

    if (!validation.valid) {
      // Filter out ID errors for CouponCode since it's auto-generated
      const filteredErrors =
        collectionName === "CouponCode"
          ? validation.errors.filter(
              (err) => !err.toLowerCase().includes("id is required"),
            )
          : validation.errors;

      if (filteredErrors.length > 0) {
        setValidationErrors(filteredErrors);
        alert(
          `Please fill in all required fields:\n${filteredErrors.join("\n")}`,
        );
        return;
      }
    }

    setIsSaving(true);
    setError("");

    try {
      if (collectionName === "ProductPrices") {
          // Save each price entry individually
          const savePromises = multiPrices.map(price => {
              const entryToSave = {
                  productId: dataToSubmit.productId,
                  countryCode: price.countryCode,
                  currency: price.currency,
                  basePrice: Number(price.basePrice),
                  offerPrice: price.offerPrice ? Number(price.offerPrice) : undefined,
                  isActive: price.isActive !== undefined ? price.isActive : true,
                  taxIncluded: price.taxIncluded !== undefined ? price.taxIncluded : true
              };
              
              return fetch(
                  getDocUrl(collectionName),
                  {
                      method: "POST",
                      headers: getAuthHeaders(),
                      body: JSON.stringify(entryToSave),
                      credentials: "include",
                  }
              );
          });
          
          const results = await Promise.all(savePromises);
          
          // Check if any request failed
          for (let i = 0; i < results.length; i++) {
              if (!results[i].ok) {
                  const txt = await results[i].text();
                  throw new Error(`Failed to save entry ${i + 1}: ${txt || results[i].status}`);
              }
          }
          
          alert("All price entries saved successfully");
          navigate(`/admin/database/${databaseType}/${collectionName}`);
          return;
      }

      const res = await fetch(
        getDocUrl(collectionName),
        {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify(dataToSubmit),
          credentials: "include",
        },
      );

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `Failed to create document (${res.status})`);
      }

      const json = await res.json();
      const createdId =
        (json.data && json.data._id) ||
        (json.data && json.data.id) ||
        json._id ||
        json.id;

      // Automatically upload transcript if selected during creation
      if (pendingTranscriptFile && createdId && collectionName === "PodcastsAudioDetails") {
          try {
              console.log("Found pending transcript file, uploading to document:", createdId);
              const formData = new FormData();
              formData.append('file', pendingTranscriptFile);
              const token = getAuthToken();
              const API_BASE_URL_STR = API_ENDPOINTS.PODCAST.ALL.replace('/podcasts', '');
              
              const uploadRes = await fetch(`${API_BASE_URL_STR}/admin/podcasts/${createdId}/upload-transcript`, {
                  method: 'POST',
                  headers: {
                      ...(token ? { Authorization: `Bearer ${token}` } : {})
                  },
                  body: formData,
                  credentials: 'include'
              });
              
              if (!uploadRes.ok) {
                  const uploadError = await uploadRes.json();
                  console.error("Transcript upload failed:", uploadError);
                  alert(`Document created, but transcript upload failed: ${uploadError.message || 'Unknown error'}`);
              } else {
                  console.log("Transcript uploaded successfully!");
              }
          } catch (uploadE) {
              console.error("Error uploading pending transcript:", uploadE);
          }
      }

      // Check if we're returning to editor (from ImageAssets creation)
      const urlParams = new URLSearchParams(location.search);
      const returnToEditor = urlParams.get("returnToEditor");
      const returnToHosts = urlParams.get("returnToHosts");
      const returnToMultiSelect = urlParams.get("returnToMultiSelect");
      const returnPath = sessionStorage.getItem("imageAssetReturnPath");
      const podcastGuestReturnPath = sessionStorage.getItem(
        "podcastGuestReturnPath",
      );
      const multiSelectReturnPath = localStorage.getItem("multiSelectReturnPath");

      if (
        returnToMultiSelect === "true" &&
        multiSelectReturnPath
      ) {
        // Return to the edit/create form with the new item ID so MultiSelectDialog can auto-select it
        localStorage.removeItem("multiSelectReturnPath");
        navigate(
          `${multiSelectReturnPath}${
            multiSelectReturnPath.includes("?") ? "&" : "?"
          }returnFromCreate=true&newId=${createdId}`,
        );
      } else if (
        returnToEditor === "true" &&
        returnPath &&
        collectionName === "ImageAssets"
      ) {
        // Return to editor with the new asset ID
        sessionStorage.removeItem("imageAssetReturnPath");
        navigate(
          `${returnPath}${
            returnPath.includes("?") ? "&" : "?"
          }returnFromCreate=true&newAssetId=${createdId}`,
        );
      } else if (
        returnToHosts === "true" &&
        podcastGuestReturnPath &&
        collectionName === "PodcastGuests"
      ) {
        // Return to hosts dialog with the new guest ID
        sessionStorage.removeItem("podcastGuestReturnPath");
        navigate(
          `${podcastGuestReturnPath}${
            podcastGuestReturnPath.includes("?") ? "&" : "?"
          }returnFromCreate=true&newGuestId=${createdId}`,
        );
      } else {
        alert("Document created successfully");
        // Navigate back to collection detail page
        navigate(`/admin/database/${databaseType}/${collectionName}`);
      }
    } catch (e) {
      setError(e.message || "Failed to create document");
      alert(`Error creating document: ${e.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    const cancelUrlParams = new URLSearchParams(location.search);
    const returnToMultiSelectCancel = cancelUrlParams.get("returnToMultiSelect");
    const multiSelectReturnPathCancel = localStorage.getItem("multiSelectReturnPath");
    const returnToEditorCancel = cancelUrlParams.get("returnToEditor");
    const imageAssetReturnPathCancel = sessionStorage.getItem("imageAssetReturnPath");

    if (returnToMultiSelectCancel === "true" && multiSelectReturnPathCancel) {
      localStorage.removeItem("multiSelectReturnPath");
      navigate(multiSelectReturnPathCancel);
    } else if (returnToEditorCancel === "true" && imageAssetReturnPathCancel) {
      sessionStorage.removeItem("imageAssetReturnPath");
      navigate(imageAssetReturnPathCancel);
    } else {
      navigate(`/admin/database/${databaseType}/${collectionName}`);
    }
  };

  // Get fields from schema only
  const schema = getCollectionSchema(collectionName);
  const fieldOrder = getFieldOrder(collectionName);
  let fieldsToShow =
    fieldOrder || (schema ? Object.keys(schema.fieldTypes) : []);

  // Filter out passwordHash and createdAt for Users collection - they're handled by backend
  if (collectionName === "Users") {
    fieldsToShow = fieldsToShow.filter(
      (field) => field !== "passwordHash" && field !== "createdAt",
    );
  }

  // Filter out updatedAt for CouponCode collection
  if (collectionName === "CouponCode" || collectionName === "couponcode") {
    fieldsToShow = fieldsToShow.filter((field) => field !== "updatedAt" && field !== "applicableCurrencies");
  }

  // Filter out uploadedAt for ImageAssets collection - it's auto-generated by backend
  if (collectionName === "ImageAssets" || collectionName === "imageassets") {
    fieldsToShow = fieldsToShow.filter((field) => field !== "uploadedAt");
  }

  // Filter out createdAt/updatedAt for Regions collection - auto-generated by backend
  if (collectionName === "Regions") {
    fieldsToShow = fieldsToShow.filter((field) => field !== "createdAt" && field !== "updatedAt");
  }

  // Filter out createdAt/updatedAt for ProductPrices collection - auto-generated by backend
  if (collectionName === "ProductPrices") {
    fieldsToShow = fieldsToShow.filter((field) => field !== "createdAt" && field !== "updatedAt");
  }

  // Filter out emailNotification, publishedAt, publishedBy for BlogContent/PodcastsAudioDetails - system-managed, not set manually on create
  if (collectionName === "BlogContent" || collectionName === "PodcastsAudioDetails") {
    fieldsToShow = fieldsToShow.filter((field) => field !== "emailNotification" && field !== "publishedAt" && field !== "publishedBy");
  }

  const isRegionsCollection = // eslint-disable-line no-unused-vars
    collectionName && collectionName.toLowerCase() === "regions";
  const isProductPricesCollection =
    collectionName && collectionName.toLowerCase() === "productprices";

  // Special handling for Content/content field in BlogContent collection
  const isBlogContentCollection =
    collectionName &&
    (collectionName.toLowerCase() === "blogcontent" ||
      collectionName.toLowerCase() === "blogcontents");
  const isProductDetailsCollection =
    collectionName && collectionName.toLowerCase() === "productdetails";
  const isPodcastsAudioDetailsCollection =
    collectionName && collectionName.toLowerCase() === "podcastsaudiodetails";
  const isCouponCodeCollection =
    collectionName && collectionName.toLowerCase() === "couponcode";
  const isAffiliateProductDetailsCollection =
    collectionName && collectionName.toLowerCase() === "affiliateproductdetails";

  return (
    <>
    <div className="admin-container w-full max-w-none px-0 pb-[20px]">
      <AdminTopNav activeTab="database" navPinned={navPinned} onTogglePin={() => setNavPinned(v => !v)} />
      <main className="admin-content">
          <div className="admin-content-pad p-[6px]">
            <div className="admin-card-full-width rounded-[12px] border border-slider bg-bg p-[14px] shadow-[0_2px_10px_rgba(0,0,0,0.06)]">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 16,
                }}
              >
                <div>
                  <Link
                    to={`/admin/database/${databaseType}/${collectionName}`}
                    style={{
                      color: "#6b7280",
                      textDecoration: "none",
                      fontSize: "14px",
                    }}
                  >
                    ← Back to {collectionName}
                  </Link>
                  <h1
                    style={{
                      fontSize: 22,
                      fontWeight: 700,
                      margin: "8px 0",
                      color: "#0f172a",
                    }}
                  >
                    Create New Document - {collectionName}
                  </h1>
                  <div style={{ fontSize: "14px", color: "#6b7280" }}>
                    {databaseName}
                  </div>
                </div>
              </div>

              {error && (
                <div
                  style={{
                    padding: "12px 16px",
                    backgroundColor: "#fef2f2",
                    border: "1px solid #fecaca",
                    borderRadius: "6px",
                    color: "#991b1b",
                    marginBottom: 16,
                  }}
                >
                  {error}
                </div>
              )}

              {validationErrors.length > 0 && (
                <div
                  style={{
                    padding: "12px 16px",
                    backgroundColor: "#fef2f2",
                    border: "1px solid #fecaca",
                    borderRadius: "6px",
                    color: "#991b1b",
                    marginBottom: 16,
                  }}
                >
                  <strong>Validation Errors:</strong>
                  <ul style={{ margin: "8px 0 0 0", paddingLeft: "20px" }}>
                    {validationErrors.map((err, idx) => (
                      <li key={idx}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}

              {loading && (
                <div className="form-skeleton-container" style={{ marginBottom: 20 }}>
                  {[
                    { labelW: '25%', inputH: 40 },
                    { labelW: '35%', inputH: 40 },
                    { labelW: '20%', inputH: 40 },
                    { labelW: '40%', inputH: 140 },
                    { labelW: '30%', inputH: 40 },
                    { labelW: '22%', inputH: 40 },
                  ].map((item, idx) => (
                    <div key={idx} className="form-skeleton-field">
                      <div className="form-skeleton-label" style={{ width: item.labelW }}></div>
                      <div 
                        className={item.inputH > 40 ? "form-skeleton-textarea" : "form-skeleton-input"} 
                        style={{ height: `${item.inputH}px` }}
                      ></div>
                    </div>
                  ))}
                </div>
              )}

              {!loading && (
                <>
                <div style={{ display: "grid", gap: 20 }}>
                  {fieldsToShow.map((key) => {
                    const fieldType = getFieldType(collectionName, key);
                    const fieldLabel = getFieldLabel(collectionName, key);
                    const isRequired = isFieldRequired(collectionName, key);
                    const hasError = validationErrors.some((err) =>
                      err.includes(fieldLabel),
                    );
                    
                    // Initialize array fields to empty array if undefined/null
                    let currentValue =
                      newData[key] !== undefined ? newData[key] : null;
                    if (
                      (fieldType === "array" || fieldType === "productarray") &&
                      (currentValue === null || currentValue === undefined)
                    ) {
                      currentValue = [];
                    }
                  
                  // Skip _id, id, __v, updatedAt, uploadedAt fields
                  // Allow createdAt for BlogContent collection only
                  if (
                    key === "_id" ||
                    key === "id" ||
                    key === "__v" ||
                    key === "updatedAt" ||
                    key === "uploadedAt"
                  )
                    return null;
                  // Special handling for ProductPrices multi-entry
                  if (isProductPricesCollection) {
                    if (['countryCode', 'currency', 'basePrice', 'offerPrice', 'taxIncluded', 'isActive'].includes(key)) {
                      if (key === 'countryCode') {
                        return (
                          <div key="multi-price-section" className="admin-card-full-width" style={{ marginTop: '20px', padding: '24px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px' }}>
                            <h2 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '20px', color: '#1e293b', borderBottom: '2px solid #e2e8f0', paddingBottom: '12px' }}>
                              Currency from Regions collection
                            </h2>
                            
                            <div style={{ display: 'grid', gap: '24px' }}>
                              {multiPrices.map((price, index) => (
                                <div key={index} style={{ 
                                  display: 'grid', 
                                  gridTemplateColumns: '1.2fr 1fr 1fr 100px 100px 40px', 
                                  gap: '12px', 
                                  alignItems: 'end',
                                  padding: '20px',
                                  backgroundColor: 'white',
                                  borderRadius: '10px',
                                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                  border: '1px solid #f1f5f9',
                                  position: 'relative'
                                }}>
                                  <div style={{ position: 'absolute', top: '-10px', left: '15px', backgroundColor: '#f8fafc', padding: '2px 8px', fontSize: '12px', fontWeight: 600, color: '#64748b', borderRadius: '4px' }}>
                                    Entry #{index + 1}
                                  </div>

                                  <div className="create-document-field" style={{ marginBottom: 0 }}>
                                    <label className="create-document-label">Region/Currency</label>
                                    <select
                                      style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px' }}
                                      value={price.countryCode}
                                      onChange={(e) => handleMultiPriceChange(index, 'countryCode', e.target.value)}
                                    >
                                      <option value="">Select Region</option>
                                      {regionOptions.map(r => {
                                        const countryCode = r.country || r.code;
                                        return (
                                          <option key={countryCode} value={countryCode}>
                                            {r.name} ({countryCode} - {r.currency})
                                          </option>
                                        );
                                      })}
                                    </select>
                                  </div>

                                  <div className="create-document-field" style={{ marginBottom: 0 }}>
                                    <label className="create-document-label">Base Price</label>
                                    <div style={{ position: 'relative' }}>
                                      <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#64748b', fontSize: '14px', fontWeight: 600 }}>
                                        {price.currencySymbol || ''}
                                      </span>
                                      <input
                                        type="number"
                                        style={{ width: '100%', padding: '10px 10px 10px 30px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px' }}
                                        value={price.basePrice}
                                        placeholder="0.00"
                                        onChange={(e) => handleMultiPriceChange(index, 'basePrice', e.target.value)}
                                      />
                                    </div>
                                  </div>

                                  <div className="create-document-field" style={{ marginBottom: 0 }}>
                                    <label className="create-document-label">Offer Price</label>
                                    <div style={{ position: 'relative' }}>
                                      <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#64748b', fontSize: '14px', fontWeight: 600 }}>
                                        {price.currencySymbol || ''}
                                      </span>
                                      <input
                                        type="number"
                                        style={{ width: '100%', padding: '10px 10px 10px 30px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px' }}
                                        value={price.offerPrice}
                                        placeholder="0.00"
                                        onChange={(e) => handleMultiPriceChange(index, 'offerPrice', e.target.value)}
                                      />
                                    </div>
                                  </div>

                                  <div className="create-document-field" style={{ marginBottom: 0 }}>
                                    <label className="create-document-label">Tax Incl.</label>
                                    <select
                                      style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px' }}
                                      value={price.taxIncluded}
                                      onChange={(e) => handleMultiPriceChange(index, 'taxIncluded', e.target.value === 'true')}
                                    >
                                      <option value="true">Yes</option>
                                      <option value="false">No</option>
                                    </select>
                                  </div>

                                  <div className="create-document-field" style={{ marginBottom: 0 }}>
                                    <label className="create-document-label">Active</label>
                                    <select
                                      style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px' }}
                                      value={price.isActive}
                                      onChange={(e) => handleMultiPriceChange(index, 'isActive', e.target.value === 'true')}
                                    >
                                      <option value="true">Yes</option>
                                      <option value="false">No</option>
                                    </select>
                                  </div>

                                  <button
                                    type="button"
                                    onClick={() => removePriceEntry(index)}
                                    style={{
                                      padding: '8px',
                                      backgroundColor: '#fee2e2',
                                      color: '#dc2626',
                                      border: 'none',
                                      borderRadius: '6px',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      transition: 'all 0.2s'
                                    }}
                                    title="Remove Entry"
                                  >
                                    <i className="fi fi-sr-trash" style={{ fontSize: '16px' }}></i>
                                  </button>
                                </div>
                              ))}
                            </div>
                            
                            <button
                              type="button"
                              onClick={addPriceEntry}
                              style={{
                                marginTop: '24px',
                                padding: '10px 20px',
                                backgroundColor: '#2563eb',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontWeight: 600,
                                fontSize: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                transition: 'all 0.2s',
                                boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.2)'
                              }}
                            >
                              <i className="fi fi-sr-plus-small"></i> Add Another Price
                            </button>
                          </div>
                        );
                      }
                      return null;
                    }
                  }

                  // Check if this is a date field that supports scheduling
                  const isDateField =
                    (collectionName === "BlogContent" && key === "createdAt") ||
                    (collectionName === "PodcastsAudioDetails" && key === "date");

                  // Scheduling Toggle Logic
                  if (isDateField) {
                    const handlePostNow = () => {
                      setIsScheduled(false);
                      // Set date to current time and mark as published
                      const now = new Date().toISOString();
                      handleInputChange(key, now);
                      handleInputChange('isPublished', true);
                      if (collectionName === "BlogContent") {
                        handleInputChange('publishedAt', now);
                      }
                    };

                    const handleSchedule = () => {
                      setIsScheduled(true);
                      const existingDate = newData[key] ? new Date(newData[key]) : null;
                      const isFuture = existingDate && !isNaN(existingDate.getTime()) && existingDate > new Date();
                      const scheduledTime = isFuture ? newData[key] : new Date(Date.now() + 3600000).toISOString();
                      handleInputChange(key, scheduledTime);
                      // Must be unpublished to hold as scheduled
                      handleInputChange('isPublished', false);
                      if (collectionName === "BlogContent") {
                        handleInputChange('publishedAt', scheduledTime);
                      }
                    };

                    const isPostNow = !isScheduled;

                    return (
                      <div
                        key={`${key}-scheduling-wrapper`}
                        className="create-document-field"
                      >
                        <div style={{ marginBottom: "12px" }}>
                          <label
                            className="create-document-label"
                            style={{ marginBottom: "8px", display: "block" }}
                          >
                            Publishing Option
                          </label>
                          <div style={{ display: "flex", gap: "12px" }}>
                            <button
                              type="button"
                              onClick={handlePostNow}
                              style={{
                                padding: "8px 16px",
                               borderRadius: "6px",
                               border: `1px solid ${isPostNow ? "#2563eb" : "#e5e7eb"}`,
                               backgroundColor: isPostNow ? "#eff6ff" : "#fff",
                               color: isPostNow ? "#2563eb" : "#374151",
                               cursor: "pointer",
                               fontWeight: 500,
                               transition: "all 0.2s",
                              }}
                            >
                              Post Now
                            </button>
                            <button
                              type="button"
                              onClick={handleSchedule}
                              style={{
                                padding: "8px 16px",
                                borderRadius: "6px",
                                border: `1px solid ${!isPostNow ? "#2563eb" : "#e5e7eb"}`,
                                backgroundColor: !isPostNow ? "#eff6ff" : "#fff",
                                color: !isPostNow ? "#2563eb" : "#374151",
                                cursor: "pointer",
                                fontWeight: 500,
                                transition: "all 0.2s",
                              }}
                            >
                              Schedule
                            </button>
                          </div>
                        </div>
                        
                        {/* Only show date input if Scheduled OR for Podcasts where backdating is common */}
                        {/* eslint-disable no-use-before-define */}
                        {(isScheduled || isPodcastsAudioDetailsCollection) && (
                            <div className="create-document-field">
                                <label className="create-document-label">
                                    {isScheduled ? "Scheduled Date & Time" : fieldLabel}
                                    {isRequired && <span style={{marginLeft: '4px', color: '#dc2626'}}>*</span>}
                                </label>                                 <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                                    <input
                                        type="datetime-local"
                                        style={{
                                            width: 'auto',
                                            maxWidth: '100%',
                                            padding: '8px 12px',
                                            border: `1px solid ${hasError ? '#dc2626' : '#d1d5db'}`,
                                            borderRadius: '6px',
                                            fontSize: '14px',
                                            backgroundColor: '#fff'
                                        }}
                                        value={newData[key] ? (() => {
                                            const date = new Date(newData[key]);
                                            // Adjust for timezone offset to display local time correctly
                                            const localDate = new Date(date.getTime() - (date.getTimezoneOffset() * 60000));
                                            return localDate.toISOString().slice(0, 16);
                                        })() : ''}
                                        onChange={(e) => {
                                             const dateVal = new Date(e.target.value).toISOString();
                                             handleInputChange(key, dateVal);
                                             if (collectionName === "BlogContent") {
                                                 handleInputChange('publishedAt', dateVal);
                                             }
                                         }}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => handleInputChange(key, new Date().toISOString())}
                                        style={{
                                            padding: '8px 12px',
                                            backgroundColor: '#eff6ff',
                                            border: '1px solid #2563eb',
                                            borderRadius: '6px',
                                            fontSize: '12px',
                                            color: '#2563eb',
                                            cursor: 'pointer',
                                            fontWeight: 500
                                        }}
                                    >
                                        Set to Now
                                    </button>
                                </div>

                            </div>
                        )}
                      </div>
                    );
                  }

                  if (key === "createdAt" && collectionName !== "BlogContent")
                    return null;

                  // Conditional rendering for stockAvailability - only show when productType is "physical" or "used"
                  if (
                    isProductDetailsCollection &&
                    key === "stockAvailability"
                  ) {
                    const productType = newData["productType"] || "";
                    if (productType !== "physical" && productType !== "used") {
                      return null; // Don't show stockAvailability if productType is not "physical" or "used"
                    }
                  }

                  // Conditional rendering for displayImage - only show when productType is "digital"
                  if (isProductDetailsCollection && key === "displayImage") {
                    const productType = newData["productType"] || "";
                    if (productType !== "digital") {
                      return null; // Don't show displayImage if productType is not "digital"
                    }
                  }

                  // Conditional rendering for CouponCode fields
                  if (isCouponCodeCollection) {
                    const discountType = newData["discountType"] || "PERCENT";
                    if (key === "discountValue" && discountType !== "PERCENT") {
                      return null;
                    }
                    if (key === "discountByCountry" && discountType !== "FIXED") {
                      return null;
                    }
                    if (key === "minimumOrderValue" && discountType !== "PERCENT") {
                      return null;
                    }
                    if (
                      key === "minimumOrderValueByCountry" &&
                      discountType !== "FIXED"
                    ) {
                      return null;
                    }
                  }

                  // Special handling for Content/content field in BlogContent collection
                  const isContentField =
                    key.toLowerCase() === "content" && isBlogContentCollection;
                  const isDescriptionField =
                    (key.toLowerCase() === "description" &&
                      isProductDetailsCollection) ||
                    (key.toLowerCase() === "description" &&
                      isCouponCodeCollection);
                  const isOverviewField = 
                    key.toLowerCase() === "overview" &&
                    isPodcastsAudioDetailsCollection;

                  if (isContentField || isDescriptionField || isOverviewField || fieldType === "tiptap" || fieldType === "richtext") {
                    // Handle different content formats similar to EditDocument
                    let tipTapContent = null;

                    if (currentValue) {
                      if (typeof currentValue === "string") {
                        try {
                          const parsed = JSON.parse(currentValue);
                          if (
                            parsed &&
                            typeof parsed === "object" &&
                            parsed.json
                          ) {
                            if (parsed.json && parsed.json.type === "doc") {
                              tipTapContent = parsed.json;
                            } else if (parsed.html) {
                              tipTapContent = parsed.html;
                            } else {
                              tipTapContent = { type: "doc", content: [] };
                            }
                          } else if (
                            parsed &&
                            typeof parsed === "object" &&
                            parsed.type === "doc"
                          ) {
                            tipTapContent = parsed;
                          } else {
                              // Split by newline and create paragraphs for each line to preserve formatting
                              const lines = String(currentValue).split('\n');
                              tipTapContent = {
                                type: "doc",
                                content: lines.map(line => ({
                                  type: "paragraph",
                                  content: line ? [{ type: "text", text: line }] : []
                                }))
                              };
                          }
                        } catch {
                          // If it's not JSON, treat as plain text and split by newline
                          const lines = String(currentValue).split('\n');
                          tipTapContent = {
                            type: "doc",
                            content: lines.map(line => ({
                              type: "paragraph",
                              content: line ? [{ type: "text", text: line }] : []
                            }))
                          };
                        }
                      } else if (typeof currentValue === "object") {
                        if (
                          currentValue.json &&
                          typeof currentValue.json === "object" &&
                          currentValue.json.type === "doc"
                        ) {
                          tipTapContent = currentValue.json;
                        } else if (currentValue.type === "doc") {
                          tipTapContent = currentValue;
                        } else if (
                          currentValue.html &&
                          typeof currentValue.html === "string"
                        ) {
                          tipTapContent = currentValue.html;
                        } else {
                          tipTapContent = currentValue;
                        }
                      }
                    }

                    if (!tipTapContent) {
                      tipTapContent = {
                        type: "doc",
                        content: [{ type: "paragraph", content: [] }],
                      };
                    }

                    const handleClearContent = () => {
                      handleInputChange(key, { type: "doc", content: [] });
                    };

                    return (
                      <div key={key} className="create-document-field">
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: "8px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <label 
                              htmlFor={`tiptap-${key}`}
                              className="create-document-label"
                            >
                              {fieldLabel}
                            </label>
                            {isRequired && (
                              <span
                                style={{
                                  display: "inline-block",
                                  padding: "2px 8px",
                                  backgroundColor: "#f3f4f6",
                                  borderRadius: "12px",
                                  fontSize: "12px",
                                  fontWeight: 500,
                                  color: "#6b7280",
                                }}
                              >
                                Required
                              </span>
                            )}
                          </div>
                          <button
                            type="button"
                            tabIndex={-1}
                            onClick={handleClearContent}
                            style={{
                              padding: "4px 12px",
                              backgroundColor: "#f3f4f6",
                              border: "none",
                              borderRadius: "12px",
                              fontSize: "12px",
                              fontWeight: 500,
                              color: "#6b7280",
                              cursor: "pointer",
                              transition: "background-color 0.2s",
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = "#e5e7eb";
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = "#f3f4f6";
                            }}
                          >
                            Clear
                          </button>
                        </div>
                        <TipTapEditor
                          value={tipTapContent}
                          navPinned={navPinned}
                          onChange={(data) => {
                            try {
                              const { json, html } = data;
                              const isSocialMedia = databaseType === 'socialmediadb';
                              
                              if (isSocialMedia) {
                                handleInputChange(key, { json, html });
                              } else {
                                // Default to just saving the JSON for other collections (like Blog)
                                handleInputChange(key, json);
                              }
                            } catch (error) {
                              handleInputChange(key, data.json || data);
                            }
                          }}
                          isBlogContent={
                            isBlogContentCollection ||
                            isPodcastsAudioDetailsCollection ||
                            isCouponCodeCollection ||
                            isAffiliateProductDetailsCollection
                          }
                        />
                        {hasError && (
                          <div className="create-document-error">
                            This field is required
                          </div>
                        )}
                      </div>
                    );
                  }

                  return (
                    <div key={key} className="create-document-field">
                      <DynamicField
                        label={fieldLabel}
                        value={currentValue}
                        onChange={(value) => handleInputChange(key, value)}
                        fieldType={fieldType}
                        fieldName={key}
                        hasError={hasError}
                        collectionName={collectionName}
                        authorOptions={authorOptions}
                        categoryOptions={
                          collectionName === "PostTopics" &&
                          key === "whichCategory"
                            ? postTopicCategoryOptions
                            : categoryOptions
                        }
                        postTopicOptions={postTopicOptions}
                        productOptions={productOptions}
                        isRequired={isRequired}
                        allData={newData}
                        onUpdateField={handleInputChange}
                        navigate={navigate}
                        location={location}
                        onBeforeNavigate={saveFormStateToSession}
                        onTranscriptFileSelect={(file) => setPendingTranscriptFile(file)}
                      />
                    </div>
                  );
                })}
              </div>

              <div
                ref={buttonBarRef}
                style={{
                  display: "flex",
                  gap: 12,
                  marginTop: 24,
                  paddingTop: 24,
                  borderTop: "1px solid #e5e7eb",
                }}
              >
                <button
                  onClick={handleCreate}
                  disabled={isSaving}
                  style={{
                    padding: "10px 20px",
                    backgroundColor: isSaving ? "#9ca3af" : "#059669",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: isSaving ? "not-allowed" : "pointer",
                    fontWeight: 600,
                    fontSize: "14px",
                  }}
                >
                  {isSaving ? "Saving..." : "Save"}
                </button>
                <button
                  onClick={handleCancel}
                  disabled={isSaving}
                  style={{
                    padding: "10px 20px",
                    backgroundColor: "#6b7280",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: isSaving ? "not-allowed" : "pointer",
                    fontWeight: 600,
                    fontSize: "14px",
                  }}
                >
                  Cancel
                </button>
              </div>
              </>
            )}
            </div>
          </div>
        </main>
    </div>

    {!loading && showFixedBar && (
      <div className="admin-fixed-action-bar fixed bottom-[12px] right-[12px] z-[60] rounded-[10px] border border-slider bg-bg p-[8px] shadow-[0_4px_16px_rgba(0,0,0,0.15)]">
        <button
          onClick={handleCreate}
          disabled={isSaving}
          style={{
            padding: '10px 20px',
            backgroundColor: isSaving ? '#9ca3af' : '#059669',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: isSaving ? 'not-allowed' : 'pointer',
            fontWeight: 600,
            fontSize: '14px',
          }}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </button>
        <button
          onClick={handleCancel}
          disabled={isSaving}
          style={{
            padding: '10px 20px',
            backgroundColor: '#6b7280',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: isSaving ? 'not-allowed' : 'pointer',
            fontWeight: 600,
            fontSize: '14px',
          }}
        >
          Cancel
        </button>
      </div>
    )}
    </>
  );
}
